import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchableSelect from '../components/SearchableSelect';
import OTPInput from '../components/OTPInput';
import { 
  ChatBubbleBottomCenterTextIcon, 
  CheckCircleIcon,
  EnvelopeIcon,
  PhoneIcon,
  UserIcon,
  BuildingOfficeIcon,
  ArrowPathIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';

const Signup = () => {
  const { register, isAuthenticated, loading: authLoading, setAuthenticatedUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    // if (!authLoading && isAuthenticated) {
    //   navigate('/dashboard');
    // }
  }, [isAuthenticated, authLoading, navigate]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [planDetails, setPlanDetails] = useState({});
  const [planDetailsLoading, setPlanDetailsLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    email: '',
    organizationName: '',
    phone: '',
    countryCode: '+233',
    businessType: 'startup',
    website: '',
    agreeToTerms: false,
    subscribeNewsletter: true
  });
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  
  // OTP state
  const [showOtpForm, setShowOtpForm] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [canResend, setCanResend] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  // Country codes for phone number (comprehensive list with all countries)
  const countryCodes = [
    // North America
    { code: '+1', country: 'United States', flag: '🇺🇸' },
    { code: '+1', country: 'Canada', flag: '🇨🇦' },
    
    // Europe
    { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
    { code: '+49', country: 'Germany', flag: '🇩🇪' },
    { code: '+33', country: 'France', flag: '🇫🇷' },
    { code: '+39', country: 'Italy', flag: '🇮🇹' },
    { code: '+34', country: 'Spain', flag: '🇪🇸' },
    { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
    { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
    { code: '+43', country: 'Austria', flag: '🇦🇹' },
    { code: '+32', country: 'Belgium', flag: '🇧🇪' },
    { code: '+45', country: 'Denmark', flag: '🇩🇰' },
    { code: '+46', country: 'Sweden', flag: '🇸🇪' },
    { code: '+47', country: 'Norway', flag: '🇳🇴' },
    { code: '+358', country: 'Finland', flag: '🇫🇮' },
    { code: '+48', country: 'Poland', flag: '🇵🇱' },
    
    // Asia Pacific
    { code: '+61', country: 'Australia', flag: '🇦🇺' },
    { code: '+81', country: 'Japan', flag: '🇯🇵' },
    { code: '+86', country: 'China', flag: '🇨🇳' },
    { code: '+91', country: 'India', flag: '🇮🇳' },
    { code: '+65', country: 'Singapore', flag: '🇸🇬' },
    { code: '+60', country: 'Malaysia', flag: '🇲🇾' },
    { code: '+66', country: 'Thailand', flag: '🇹🇭' },
    { code: '+84', country: 'Vietnam', flag: '🇻🇳' },
    { code: '+62', country: 'Indonesia', flag: '🇮🇩' },
    { code: '+63', country: 'Philippines', flag: '🇵🇭' },
    { code: '+82', country: 'South Korea', flag: '🇰🇷' },
    { code: '+852', country: 'Hong Kong', flag: '🇭🇰' },
    
    // Americas
    { code: '+55', country: 'Brazil', flag: '🇧🇷' },
    { code: '+52', country: 'Mexico', flag: '🇲🇽' },
    { code: '+54', country: 'Argentina', flag: '🇦🇷' },
    { code: '+56', country: 'Chile', flag: '🇨🇱' },
    { code: '+57', country: 'Colombia', flag: '🇨🇴' },
    { code: '+51', country: 'Peru', flag: '🇵🇪' },
    { code: '+58', country: 'Venezuela', flag: '🇻🇪' },
    
    // Africa (comprehensive list)
    { code: '+27', country: 'South Africa', flag: '🇿🇦' },
    { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
    { code: '+254', country: 'Kenya', flag: '🇰🇪' },
    { code: '+233', country: 'Ghana', flag: '🇬🇭' },
    { code: '+256', country: 'Uganda', flag: '🇺🇬' },
    { code: '+255', country: 'Tanzania', flag: '🇹🇿' },
    { code: '+251', country: 'Ethiopia', flag: '🇪🇹' },
    { code: '+212', country: 'Morocco', flag: '🇲🇦' },
    { code: '+20', country: 'Egypt', flag: '🇪🇬' },
    { code: '+213', country: 'Algeria', flag: '🇩🇿' },
    { code: '+216', country: 'Tunisia', flag: '🇹🇳' },
    { code: '+218', country: 'Libya', flag: '🇱🇾' },
    { code: '+249', country: 'Sudan', flag: '🇸🇩' },
    { code: '+211', country: 'South Sudan', flag: '🇸🇸' },
    { code: '+225', country: 'Ivory Coast', flag: '🇨🇮' },
    { code: '+221', country: 'Senegal', flag: '🇸🇳' },
    { code: '+223', country: 'Mali', flag: '🇲🇱' },
    { code: '+226', country: 'Burkina Faso', flag: '🇧🇫' },
    { code: '+227', country: 'Niger', flag: '🇳🇪' },
    { code: '+228', country: 'Togo', flag: '🇹🇬' },
    { code: '+229', country: 'Benin', flag: '🇧🇯' },
    { code: '+230', country: 'Mauritius', flag: '🇲🇺' },
    { code: '+231', country: 'Liberia', flag: '🇱🇷' },
    { code: '+232', country: 'Sierra Leone', flag: '🇸🇱' },
    { code: '+235', country: 'Chad', flag: '🇹🇩' },
    { code: '+236', country: 'Central African Republic', flag: '🇨🇫' },
    { code: '+237', country: 'Cameroon', flag: '🇨🇲' },
    { code: '+238', country: 'Cape Verde', flag: '🇨🇻' },
    { code: '+239', country: 'São Tomé and Príncipe', flag: '🇸🇹' },
    { code: '+240', country: 'Equatorial Guinea', flag: '🇬🇶' },
    { code: '+241', country: 'Gabon', flag: '🇬🇦' },
    { code: '+242', country: 'Republic of the Congo', flag: '🇨🇬' },
    { code: '+243', country: 'Democratic Republic of the Congo', flag: '🇨🇩' },
    { code: '+244', country: 'Angola', flag: '🇦🇴' },
    { code: '+245', country: 'Guinea-Bissau', flag: '🇬🇼' },
    { code: '+246', country: 'British Indian Ocean Territory', flag: '🇮🇴' },
    { code: '+248', country: 'Seychelles', flag: '🇸🇨' },
    { code: '+250', country: 'Rwanda', flag: '🇷🇼' },
    { code: '+252', country: 'Somalia', flag: '🇸🇴' },
    { code: '+253', country: 'Djibouti', flag: '🇩🇯' },
    { code: '+257', country: 'Burundi', flag: '🇧🇮' },
    { code: '+258', country: 'Mozambique', flag: '🇲🇿' },
    { code: '+260', country: 'Zambia', flag: '🇿🇲' },
    { code: '+261', country: 'Madagascar', flag: '🇲🇬' },
    { code: '+262', country: 'Mayotte', flag: '🇾🇹' },
    { code: '+263', country: 'Zimbabwe', flag: '🇿🇼' },
    { code: '+264', country: 'Namibia', flag: '🇳🇦' },
    { code: '+265', country: 'Malawi', flag: '🇲🇼' },
    { code: '+266', country: 'Lesotho', flag: '🇱🇸' },
    { code: '+267', country: 'Botswana', flag: '🇧🇼' },
    { code: '+268', country: 'Eswatini', flag: '🇸🇿' },
    { code: '+269', country: 'Comoros', flag: '🇰🇲' },
    { code: '+290', country: 'Saint Helena', flag: '🇸🇭' },
    { code: '+291', country: 'Eritrea', flag: '🇪🇷' },
    
    // Middle East
    { code: '+971', country: 'UAE', flag: '🇦🇪' },
    { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
    { code: '+972', country: 'Israel', flag: '🇮🇱' },
    { code: '+974', country: 'Qatar', flag: '🇶🇦' },
    { code: '+973', country: 'Bahrain', flag: '🇧🇭' },
    { code: '+965', country: 'Kuwait', flag: '🇰🇼' },
    { code: '+968', country: 'Oman', flag: '🇴🇲' },
    { code: '+961', country: 'Lebanon', flag: '🇱🇧' },
    { code: '+962', country: 'Jordan', flag: '🇯🇴' },
    { code: '+963', country: 'Syria', flag: '🇸🇾' },
    { code: '+964', country: 'Iraq', flag: '🇮🇶' },
    { code: '+98', country: 'Iran', flag: '🇮🇷' },
    { code: '+90', country: 'Turkey', flag: '🇹🇷' },
  ].sort((a, b) => a.country.localeCompare(b.country));

  const businessTypes = [
    { value: 'startup', label: 'Startup' },
    { value: 'smb', label: 'Small/Medium Business' },
    { value: 'enterprise', label: 'Enterprise' },
    { value: 'agency', label: 'Digital Agency' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'other', label: 'Other' }
  ];

  // Transform country codes for SearchableSelect
  const countryOptions = useMemo(() => 
    countryCodes.map(({ code, country, flag }) => ({
      value: code,
      label: `${code} - ${country}`,
      icon: flag,
      description: country
    })), [countryCodes]
  );

  useEffect(() => {
    const fetchPlansAndSetSelected = async () => {
      try {
        // Fetch packages from API
        const response = await api.getPackages();
        if (response.data.success) {
          const plans = {};
          response.data.packages.forEach(pkg => {
            plans[pkg.id] = {
              name: pkg.name,
              price: pkg.price_usd,
              description: pkg.description
            };
          });
          setPlanDetails(plans);

          // Get plan from URL parameter
          const planParam = searchParams.get('plan');
          if (planParam && plans[planParam]) {
            setSelectedPlan(planParam);
          }
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
        // Fallback plan details
        setPlanDetails({
          starter: { name: 'Starter', price: 9.99, description: 'Perfect for small businesses' },
          professional: { name: 'Professional', price: 19.99, description: 'Ideal for growing businesses' },
          business: { name: 'Business', price: 299, description: 'For enterprises needing scale' }
        });
      } finally {
        setPlanDetailsLoading(false);
      }
    };

    fetchPlansAndSetSelected();
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleOtpChange = (value) => {
    setOtpCode(value);
    setOtpError('');
  };

  const handleOtpComplete = async (code) => {
    if (code.length !== 6) return;
    
    setOtpLoading(true);
    setOtpError('');
    
    try {
      const response = await api.verifyOTP(formData.email, code, 'signup');
      
      if (response.data.success) {
        setOtpVerified(true);
        // Update auth context with user data
        setAuthenticatedUser(response.data.user, response.data.organization);
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1000);
      }
    } catch (error) {
      setOtpError(error.response?.data?.error || 'Invalid OTP code. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!canResend || resendLoading) return;
    
    setResendLoading(true);
    setOtpError('');
    
    try {
      const response = await api.resendOTP(formData.email, 'signup');
      
      if (response.data.success) {
        setOtpCode('');
        setOtpError('');
        // Start resend countdown
        setCanResend(false);
        setResendCountdown(120); // 2 minutes
      } else {
        setOtpError(response.data.error || 'Failed to resend OTP');
      }
    } catch (error) {
      setOtpError(error.response?.data?.error || 'Failed to resend OTP');
    } finally {
      setResendLoading(false);
    }
  };

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (resendCountdown === 0 && !canResend) {
      setCanResend(true);
    }
  }, [resendCountdown, canResend]);

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.organizationName.trim()) {
      newErrors.organizationName = 'Organization name is required';
    }

    // Phone validation (required)
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      const phoneRegex = /^[0-9-\s\(\)\.]+$/;
      if (!phoneRegex.test(formData.phone)) {
        newErrors.phone = 'Invalid phone number format';
      }
    }

    // Website validation (if provided)
    if (formData.website.trim() && !formData.website.match(/^https?:\/\/.+/)) {
      newErrors.website = 'Website must start with http:// or https://';
    }

    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors({});
    
    try {
      const signupData = {
        firstName: 'User', // Placeholder - will be updated in profile
        lastName: 'Account', // Placeholder - will be updated in profile  
        email: formData.email,
        organizationName: formData.organizationName,
        phone: `${formData.countryCode}${formData.phone.replace(/[^0-9]/g, '')}`,
        businessType: formData.businessType,
        website: formData.website,
        selectedPlan: selectedPlan
      };

      const result = await register(signupData);
      
      if (result.success) {
        setRegistrationSuccess(true);
        // Start resend countdown for OTP
        setResendCountdown(120);
        setCanResend(false);
      } else {
        setErrors({ submit: result.error });
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors({ submit: 'An unexpected error occurred. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-whatsapp-50 via-green-50 to-emerald-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Logo and Header */}
        <div className="text-center">
          <Link to="/" className="inline-flex justify-center">
            <div className="flex items-center justify-center h-16 w-16 bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-2xl shadow-lg">
              <ChatBubbleBottomCenterTextIcon className="h-8 w-8 text-white" />
            </div>
          </Link>
          <h1 className="mt-6 text-3xl font-bold text-gray-900">
            Join Snwolley
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Start your Snwolley Business journey today
          </p>
        </div>

        {/* Form */}
        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100">
          {registrationSuccess ? (
            /* Success Message with OTP Option */
            <div className="text-center space-y-6">
              {otpVerified ? (
                /* OTP Verified Success */
                <div className="text-center space-y-6">
                  <div className="flex justify-center">
                    <div className="flex items-center justify-center h-16 w-16 bg-green-100 rounded-full">
                      <CheckCircleIcon className="h-8 w-8 text-green-600 animate-pulse" />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-xl font-semibold text-gray-900">Welcome to Snwolley!</h3>
                    <p className="text-sm text-gray-600">
                      Your account has been verified. Redirecting to your dashboard...
                    </p>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="text-sm text-green-700">
                      <p className="font-medium">✨ Verification Complete</p>
                      <p className="mt-1">Setting up your dashboard...</p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-center">
                    <div className="flex items-center justify-center h-16 w-16 bg-green-100 rounded-full">
                      <CheckCircleIcon className="h-8 w-8 text-green-600" />
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <h3 className="text-xl font-semibold text-gray-900">Account Created!</h3>
                    <p className="text-sm text-gray-600">
                      Welcome <strong>{formData.organizationName}</strong>! We've sent verification options to <strong>{formData.email}</strong>
                    </p>
                  </div>

                  {/* Authentication Options */}
                  <div className="space-y-4">
                    {!showOtpForm ? (
                      /* Show Options */
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <EnvelopeIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                            <div className="text-sm text-blue-700">
                              <p className="font-medium">Check your email</p>
                              <p className="mt-1">Click "Access Your Dashboard" in the welcome email to get started.</p>
                            </div>
                          </div>
                        </div>

                        <div className="text-center">
                          <p className="text-sm text-gray-500 mb-3">Or use the 6-digit code from your email</p>
                          <button
                            onClick={() => setShowOtpForm(true)}
                            disabled={otpLoading}
                            className="inline-flex items-center px-6 py-3 border border-whatsapp-500 text-sm font-medium rounded-lg text-whatsapp-600 bg-whatsapp-50 hover:bg-whatsapp-100 focus:outline-none focus:ring-2 focus:ring-whatsapp-500 transition-all duration-200"
                          >
                            <ArrowRightIcon className="h-4 w-4 mr-2" />
                            Enter OTP Code
                          </button>
                        </div>

                        <div className="space-y-3">
                          <Link
                            to="/login"
                            className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-whatsapp-500 to-green-600 hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
                          >
                            Go to Sign In
                          </Link>
                          <p className="text-xs text-gray-500">
                            Didn't receive the email? Check your spam folder or contact support.
                          </p>
                        </div>
                      </div>
                    ) : (
                      /* OTP Form */
                      <div className="space-y-6">
                        <button
                          onClick={() => setShowOtpForm(false)}
                          className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                        >
                          ← Back to options
                        </button>

                        <div className="bg-whatsapp-50 border border-whatsapp-200 rounded-lg p-4">
                          <div className="flex items-start">
                            <EnvelopeIcon className="h-5 w-5 text-whatsapp-600 mt-0.5 mr-3 flex-shrink-0" />
                            <div className="text-sm text-whatsapp-700">
                              <p className="font-medium">Enter your 6-digit code</p>
                              <p className="mt-1">Check your email for the verification code.</p>
                            </div>
                          </div>
                        </div>

                        <OTPInput
                          length={6}
                          value={otpCode}
                          onChange={handleOtpChange}
                          onComplete={handleOtpComplete}
                          disabled={otpLoading}
                          error={otpError}
                          autoFocus={true}
                        />

                        {/* Resend OTP */}
                        <div className="text-center">
                          {canResend ? (
                            <button
                              onClick={handleResendOtp}
                              disabled={resendLoading}
                              className="inline-flex items-center text-sm text-whatsapp-600 hover:text-whatsapp-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {resendLoading ? (
                                <>
                                  <ArrowPathIcon className="h-4 w-4 mr-1 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <ArrowPathIcon className="h-4 w-4 mr-1" />
                                  Resend Code
                                </>
                              )}
                            </button>
                          ) : resendCountdown > 0 ? (
                            <p className="text-sm text-gray-500">
                              Resend available in {Math.floor(resendCountdown / 60)}:{(resendCountdown % 60).toString().padStart(2, '0')}
                            </p>
                          ) : null}
                        </div>

                        {/* Alternative Option */}
                        <div className="text-center pt-4 border-t border-gray-200">
                          <p className="text-sm text-gray-500 mb-2">
                            Prefer the magic link?
                          </p>
                          <button
                            onClick={() => setShowOtpForm(false)}
                            className="text-sm text-whatsapp-600 hover:text-whatsapp-700 transition-colors"
                          >
                            Check your email for the login link instead
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          ) : (
            /* Complete Signup Form */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Create Your Account</h2>
                <p className="text-sm text-gray-600 mt-2">Tell us about yourself and your business</p>
              </div>

              {/* Selected Plan Display */}
              {selectedPlan && !planDetailsLoading && planDetails[selectedPlan] && searchParams.get('plan') && (
                <div className="bg-whatsapp-50 border border-whatsapp-200 rounded-lg p-4 mb-6">
                  <div className="text-center">
                    <h3 className="text-sm font-medium text-whatsapp-800">Selected Plan</h3>
                    <div className="mt-2">
                      <span className="text-lg font-bold text-whatsapp-900">{planDetails[selectedPlan].name}</span>
                      <span className="text-whatsapp-700 ml-2">${planDetails[selectedPlan].price}/month</span>
                    </div>
                    <p className="text-xs text-whatsapp-600 mt-1">{planDetails[selectedPlan].description}</p>
                  </div>
                </div>
              )}

              {/* Plan Loading State */}
              {planDetailsLoading && searchParams.get('plan') && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 animate-pulse">
                  <div className="text-center">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded mb-1"></div>
                    <div className="h-3 bg-gray-200 rounded"></div>
                  </div>
                </div>
              )}

              {errors.submit && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {errors.submit}
                </div>
              )}


              {/* Email Field */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <EnvelopeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="your@email.com"
                  />
                </div>
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Organization Information */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <BuildingOfficeIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={formData.organizationName}
                    onChange={(e) => handleInputChange('organizationName', e.target.value)}
                    className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 ${
                      errors.organizationName ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="Npontu Technologies"
                  />
                </div>
                {errors.organizationName && <p className="text-red-500 text-sm mt-1">{errors.organizationName}</p>}
              </div>

              {/* Phone Number with Country Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex space-x-2">
                  <div className="w-48">
                    <SearchableSelect
                      options={countryOptions}
                      value={formData.countryCode}
                      onChange={(e) => handleInputChange('countryCode', e.target.value)}
                      placeholder="Select country..."
                      isSearchable={true}
                      error={errors.countryCode}
                      name="countryCode"
                    />
                  </div>
                  <div className="relative flex-1">
                    <PhoneIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      className={`w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 ${
                        errors.phone ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="555 123 4567"
                    />
                  </div>
                </div>
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              {/* Business Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Business Type
                </label>
                <select
                  value={formData.businessType}
                  onChange={(e) => handleInputChange('businessType', e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500"
                >
                  {businessTypes.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>

              {/* Website */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Website (Optional)
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  className={`w-full px-3 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-whatsapp-500 focus:border-whatsapp-500 ${
                    errors.website ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="https://yourwebsite.com"
                />
                {errors.website && <p className="text-red-500 text-sm mt-1">{errors.website}</p>}
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="text-sm text-blue-700">
                    <p className="font-medium">🚀 What happens next?</p>
                    <ul className="mt-1 list-disc list-inside space-y-1">
                      <li>We'll send you a secure login link via email</li>
                      <li>Your organization will be created instantly</li>
                      <li>Start building WhatsApp automations right away</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Terms */}
              <div className="space-y-4">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.agreeToTerms}
                    onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
                    className="mt-1 h-4 w-4 text-whatsapp-600 focus:ring-whatsapp-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    I agree to the{' '}
                    <button type="button" className="text-whatsapp-600 hover:text-whatsapp-500 underline">
                      Terms of Service
                    </button>{' '}
                    and{' '}
                    <button type="button" className="text-whatsapp-600 hover:text-whatsapp-500 underline">
                      Privacy Policy
                    </button>
                  </span>
                </label>
                {errors.agreeToTerms && <p className="text-red-500 text-sm">{errors.agreeToTerms}</p>}

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.subscribeNewsletter}
                    onChange={(e) => handleInputChange('subscribeNewsletter', e.target.checked)}
                    className="mt-1 h-4 w-4 text-whatsapp-600 focus:ring-whatsapp-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm text-gray-700">
                    Send me product updates and Snwolley Business API tips
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full px-8 py-3 text-sm font-medium text-white bg-gradient-to-r from-whatsapp-500 to-green-600 rounded-lg hover:from-whatsapp-600 hover:to-green-700 transform hover:scale-[1.02] transition-all duration-200 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Creating account...
                  </div>
                ) : (
                  'Create Account & Send Secure Link'
                )}
              </button>
            </form>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-whatsapp-600 hover:text-whatsapp-700 font-medium">
              Sign in
            </Link>
          </p>
          <p className="text-xs text-gray-400 mt-2">
            © 2025 Snwolley. All rights reserved.
          </p>
        </div>
      </div>

      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-10 -left-10 w-40 h-40 bg-whatsapp-200 rounded-full opacity-20 animate-pulse"></div>
        <div className="absolute top-1/3 -right-20 w-60 h-60 bg-green-200 rounded-full opacity-20 animate-pulse delay-1000"></div>
        <div className="absolute -bottom-20 left-1/4 w-80 h-80 bg-emerald-200 rounded-full opacity-20 animate-pulse delay-2000"></div>
      </div>
    </div>
  );
};

export default Signup;
